import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { fieldVisible, type Me } from '../auth/me';
import { todayInWarsaw } from '../events/calendar';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import { AssigneeField } from './AssigneeField';
import { dayTime, TaskDeliveries } from './TaskDeliveries';
import { TaskReminders } from './TaskReminders';
import { formatPln } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { changedBody, costError, draftOf, formatDue, isOverdue, REPEAT_LABELS, scopeOf, statusText, TASKS_KEY, TASKS_RESOURCE, type Draft, type Repeat, type Task } from './types';
import './tasks.css';
import { DialogLayer, useReturnFocus } from '../ui/Dialog';
import { leaveGuarded, useUnsavedGuard } from '../ui/unsaved';

/**
 * One task in a dialog: every field for whoever may edit all tasks; for a task given to me (scope `own`) only
 * "Zrobione" and the note; read-only without the right to edit. Used by "Zadania" and by the calendar's task marks.
 */
export function TaskDialog({ me, taskId, onClose }: { me: Me; taskId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const task = useQuery({ queryKey: [...TASKS_KEY, 'task', taskId], retry: false, queryFn: () => api<Task>(`/api/company-tasks/${taskId}`) });
  // the opener, recorded once before the dialog takes the focus (its layers come and go while it loads: review M5)
  useReturnFocus();
  const box = useRef<HTMLDivElement>(null);
  // Escape and the focus going back: `DialogLayer` (in the form while it is shown)
  useEffect(() => {
    box.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (box.current) trapTab(box.current, e);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="dialog-backdrop" onClick={(e) => e.target === e.currentTarget && leaveGuarded(onClose, box.current)}>
      <div className="dialog panel ct-dialog" role="dialog" aria-modal="true" aria-labelledby="ct-dialog-title" tabIndex={-1} ref={box}>
        {!task.data && <DialogLayer onClose={onClose} returnFocus={false} />}
        {task.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
        {task.error && (
          <>
            <h2 id="ct-dialog-title">Zadanie</h2>
            <p className="alert" role="alert">
              {task.error instanceof ApiError && task.error.status === 404 ? 'To zadanie zostało usunięte albo nie masz do niego dostępu.' : 'Nie udało się wczytać zadania'}
            </p>
            <div className="actions">
              <button className="btn" type="button" onClick={onClose}>
                Zamknij
              </button>
            </div>
          </>
        )}
        {task.data && (
          <TaskForm
            key={task.data.version}
            me={me}
            task={task.data}
            onClose={onClose}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: TASKS_KEY })}
          />
        )}
      </div>
    </div>
  );
}

function TaskForm({ me, task, onClose, onSaved }: { me: Me; task: Task; onClose: () => void; onSaved: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftOf(task));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const edit = scopeOf(me, 'edit');
  const mine = task.assigneeUserId === me.id;
  const full = edit === 'all';
  // the person responsible may always tick their task off and add a note (with or without a grant)
  const tickOnly = !full && mine;
  // reminding is editing the task (server: "Edycja"); a done task or one without a person has nobody to remind
  const mayRemind = (edit === 'all' || (edit === 'own' && mine)) && !!task.assigneeUserId && !task.done;
  const canDelete = scopeOf(me, 'delete') === 'all' || (scopeOf(me, 'delete') === 'own' && mine);
  const visible = (f: string) => fieldVisible(me, TASKS_RESOURCE, f);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Task>(`/api/company-tasks/${task.id}`, { method: 'PATCH', body: { version: task.version, ...body } }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  // "Przyjmuję": the answer of the person responsible (stops the repeated reminders)
  const accept = useMutation({
    mutationFn: () => api<Task>(`/api/company-tasks/${task.id}/accept`, { method: 'POST' }),
    onSuccess: () => onSaved(),
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/company-tasks/${task.id}?version=${task.version}`, { method: 'DELETE' }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const error = save.error ?? remove.error ?? accept.error;
  const conflict = error instanceof ApiError && error.status === 409;
  const issues = error instanceof ApiError ? fieldIssues(error) : [];
  const issue = (path: string) => issues.find((i) => i.path === path)?.message;
  const overdue = isOverdue(task, todayInWarsaw());
  const costProblem = visible('costGrosze') ? costError(draft.cost) : null;
  const body = changedBody(task, draft, (f) => visible(f) && (full || (tickOnly && (f === 'done' || f === 'doneNote'))));
  // owner 2026-10-23 13:34: closing (Escape, a click beside) with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const formEl = useRef<HTMLFormElement | null>(null);
  const guardRef = useUnsavedGuard({
    dirty: Object.keys(body).length > 0,
    save: async () => {
      if (costProblem) return false;
      await save.mutateAsync(body);
      return true;
    },
  });

  return (
    <form
      ref={(el) => ((formEl.current = el), guardRef(el))}
      onSubmit={(e) => {
        e.preventDefault();
        if (costProblem) return;
        if (Object.keys(body).length === 0) onClose();
        else save.mutate(body);
      }}
    >
      <DialogLayer onClose={() => leaveGuarded(onClose, formEl.current)} returnFocus={false} busy={save.isPending || remove.isPending} />
      <h2 id="ct-dialog-title">{full ? 'Zadanie' : task.title}</h2>
      {!full && (
        <dl className="ct-facts">
          <dt>Termin</dt>
          <dd className={overdue ? 'ct-overdue' : undefined}>
            {formatDue(task)}
            {overdue && ' · zaległe'}
          </dd>
          {task.assigneeUserId !== undefined && (
            <>
              <dt>Odpowiedzialny</dt>
              <dd>{task.assigneeName ?? '—'}</dd>
            </>
          )}
          {task.repeat && (
            <>
              <dt>Powtarzanie</dt>
              <dd>{REPEAT_LABELS[task.repeat]}</dd>
            </>
          )}
          {typeof task.costGrosze === 'number' && (
            <>
              <dt>Koszt</dt>
              <dd>
                {formatPln(task.costGrosze)} netto{task.costCategory ? ` · ${task.costCategory}` : ''}
              </dd>
            </>
          )}
          {task.description && (
            <>
              <dt>Opis</dt>
              <dd className="ct-description">{task.description}</dd>
            </>
          )}
        </dl>
      )}
      {full && (
        <div className="ct-form">
          {visible('title') && (
            <label className="field">
              <span>Tytuł</span>
              <input value={draft.title} onChange={(e) => set('title', e.target.value)} maxLength={200} required autoComplete="off" />
              {issue('title') && <span className="field-error">{issue('title')}</span>}
            </label>
          )}
          {visible('description') && (
            <label className="field">
              <span>Opis</span>
              <textarea value={draft.description} onChange={(e) => set('description', e.target.value)} rows={3} maxLength={5000} />
            </label>
          )}
          <div className="ct-row">
            {visible('dueDay') && (
              <label className="field">
                <span>Termin</span>
                <input type="date" value={draft.dueDay} onChange={(e) => set('dueDay', e.target.value)} required className={overdue ? 'ct-overdue' : undefined} />
                {issue('dueDay') && <span className="field-error">{issue('dueDay')}</span>}
              </label>
            )}
            {visible('dueTime') && (
              <label className="field">
                <span>Godzina (opcjonalnie)</span>
                <input type="time" value={draft.dueTime} onChange={(e) => set('dueTime', e.target.value)} />
              </label>
            )}
          </div>
          {visible('assigneeUserId') && <AssigneeField me={me} value={draft.assigneeUserId} currentName={task.assigneeName} onChange={(v) => set('assigneeUserId', v)} />}
          {(visible('costGrosze') || visible('costCategory')) && (
            <div className="ct-row">
              {visible('costGrosze') && (
                <label className="field">
                  <span>Koszt netto, zł (opcjonalnie)</span>
                  <NumberInput kind="money" value={draft.cost} onValueChange={(v) => set('cost', v)} placeholder="np. 450,00" aria-invalid={!!costProblem} />
                  {(costProblem ?? issue('costGrosze')) && <span className="field-error">{costProblem ?? issue('costGrosze')}</span>}
                </label>
              )}
              {visible('costCategory') && (
                <label className="field">
                  <span>Rodzaj kosztu</span>
                  <input value={draft.costCategory} onChange={(e) => set('costCategory', e.target.value)} maxLength={100} placeholder="np. odpady, odzież" autoComplete="off" />
                </label>
              )}
            </div>
          )}
          {visible('repeat') && (
            <label className="field">
              <span>Powtarzanie</span>
              <select value={draft.repeat} onChange={(e) => set('repeat', e.target.value as '' | Repeat)}>
                <option value="">Nie powtarzaj</option>
                {(Object.keys(REPEAT_LABELS) as Repeat[]).map((r) => (
                  <option key={r} value={r}>
                    {REPEAT_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
      {visible('done') && (
        <div className="ct-done">
          <label className="ct-check">
            <input type="checkbox" checked={draft.done} disabled={!full && !tickOnly} onChange={(e) => set('done', e.target.checked)} />
            Zrobione
          </label>
          {task.done && <span className="muted"> {statusText(task)}</span>}
          {mine && !task.done && task.acceptedAt === null && (
            <button type="button" className="btn small ct-accept" disabled={accept.isPending} onClick={() => accept.mutate()}>
              Przyjmuję
            </button>
          )}
          {!task.done && task.acceptedAt && <span className="muted"> Przyjęte {dayTime(task.acceptedAt)}</span>}
          {visible('doneNote') && (full || tickOnly || task.doneNote) && (
            <label className="field">
              <span>Notatka wykonania</span>
              <textarea value={draft.doneNote} readOnly={!full && !tickOnly} onChange={(e) => set('doneNote', e.target.value)} rows={2} maxLength={1000} placeholder="np. co zrobiono, ile kosztowało" />
            </label>
          )}
        </div>
      )}
      {mayRemind && <TaskReminders taskId={task.id} dueDayVisible={visible('dueDay')} />}
      {/* what went to the person and whether it came back: theirs, or for whoever sees who is responsible (rule 7a) */}
      {task.assigneeUserId && (mine || visible('assigneeUserId')) && <TaskDeliveries taskId={task.id} />}
      {conflict && (
        <ConflictNotice
          onReload={() => {
            void queryClient.invalidateQueries({ queryKey: TASKS_KEY });
            save.reset();
            remove.reset();
          }}
        />
      )}
      {error && !conflict && issues.length === 0 && (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Nie udało się zapisać'}
        </p>
      )}
      <div className="actions ct-actions">
        {(full || tickOnly) && (
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        )}
        <button className="btn" type="button" onClick={onClose}>
          {full || tickOnly ? 'Anuluj' : 'Zamknij'}
        </button>
        {canDelete && (
          <button className="btn ghost ct-delete" type="button" disabled={remove.isPending} onClick={() => (confirmDelete ? remove.mutate() : setConfirmDelete(true))}>
            {confirmDelete ? 'Kliknij, aby usunąć' : 'Usuń'}
          </button>
        )}
      </div>
    </form>
  );
}
